import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import Link from '@tiptap/extension-link';
import { all, createLowlight } from 'lowlight';
import styles from './richTextEditor.module.scss';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import classNames from 'classnames';

const lowlight = createLowlight(all);

export interface IRichTextEditor {
    getMarkdownContent(): string | undefined;
    getText(): string;
    clearContent(): void;
    setContent(content: string): void;
}

interface RichTextEditorProps {
    onSubmit?(content: string): void;
    onTextChanged?(editor: IRichTextEditor): void;
    handleKeyDown?(editor: IRichTextEditor, event: KeyboardEvent): boolean;
    placeholder?: string;
    isReadOnly?: boolean;
    content?: string;
    autofocus?: boolean;
    editorContentClassName?: string;
    textEditorContent?: string;
    onEditorReady?: (editor: IRichTextEditor) => void;
}
export class TipTipEditor implements IRichTextEditor {
    constructor(private editor: Editor) {}
    setContent(content: string): void {
        this.editor.commands.setContent(content);
    }
    getText(): string {
        return this.editor.getText();
    }
    clearContent(): void {
        this.editor.commands.clearContent();
    }
    getMarkdownContent(): string | undefined {
        return this.editor.storage?.markdown?.getMarkdown();
    }
}

export const RichTextEditor: React.FC<PropsWithChildren<RichTextEditorProps>> = (props) => {
    const {
        onEditorReady,
        isReadOnly,
        autofocus,
        handleKeyDown,
        content,
        placeholder,
        textEditorContent,
        onTextChanged,
        editorContentClassName,
    } = props;
    const editorRef = useRef<IRichTextEditor | null>(null);
    const editor = useEditor({
        autofocus,
        editable: !isReadOnly,
        extensions: [
            StarterKit.configure({
                bulletList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
                orderedList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
                codeBlock: false,
            }),
            CodeBlockLowlight.configure({
                lowlight,
                HTMLAttributes: {},
            }),
            Placeholder.configure({
                placeholder,
                emptyEditorClass: styles.isEmpty,
            }),
            Markdown.configure({
                tightLists: true,
                transformPastedText: true,
                transformCopiedText: false,
            }),
            Link.configure({
                openOnClick: true,
            }),
        ],
        content,
        editorProps: {
            attributes: {
                class: classNames(styles.editorAttributes, textEditorContent),
            },
            handleKeyDown: (view, event) => {
                if (!editorRef.current || !editor) return false;
                let result = false;
                const isEnter = event.key === 'Enter';
                const isInBulletList = editor.isActive('bulletList');
                const isInOrderedList = editor.isActive('orderedList');
                if (isEnter && event.shiftKey) {
                    if (isInBulletList || isInOrderedList) {
                        event.preventDefault();
                        result = true;
                        editor.chain().focus().splitListItem('listItem').run();
                    }
                } else {
                    result = handleKeyDown?.(editorRef.current, event) ?? false;
                }

                return result;
            },
        },
        onUpdate: ({ editor }) => {
            if (!editorRef.current || !editor) return false;
            onTextChanged?.(editorRef.current);
            checkForMarkdownSyntax(editor);
        },
    });

    useEffect(() => {
        if (editor) {
            editorRef.current = new TipTipEditor(editor);
            onEditorReady?.(editorRef.current);
        }
    }, [editor, onEditorReady]);

    const checkForMarkdownSyntax = (editor: any) => {
        if (!editor) return;

        const { state } = editor;
        const { selection } = state;
        const { $anchor } = selection;

        const lineStart = $anchor.start();
        const cursorPos = $anchor.pos;
        const currentLineText = state.doc.textBetween(lineStart, cursorPos);

        if (currentLineText.endsWith('```')) {
            editor.commands.deleteRange({
                from: cursorPos - 3,
                to: cursorPos,
            });

            editor.commands.setCodeBlock();
            return;
        }
    };
    return (
        <EditorContent
            editor={editor}
            className={classNames(
                styles.editorContent,
                editorContentClassName && !isReadOnly ? editorContentClassName : undefined,
            )}
        />
    );
};
